/* eslint-disable react-refresh/only-export-components */
/**
 * ModeContext - Workflow Mode Management
 *
 * Tracks the user's current workflow mode:
 * - Explore (internally "workspace"): Understanding what needs attention
 * - Build: Designing and committing to a campaign plan
 * - Run: Executing and observing a live campaign
 *
 * This context provides the "workflow spine" that connects
 * all Nexus experiences into a coherent narrative.
 *
 * Build session persistence:
 * - Auto-saves wizard state to database (debounced 500ms)
 * - Recovers incomplete sessions on page load
 * - Clears draft on successful launch or explicit cancel
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { useAuth } from "./AuthContext";
import { useClient } from "./ClientContext";
import {
  buildSessionPersistenceService,
  type BuildSessionDraft,
  type WizardState,
  type SaveStatus,
} from "../services/buildSessionPersistenceService";
import { getClientSlug } from "../types/client";
import { buildClientPath } from "../utils/routes";

// ============================================================================
// TYPES
// ============================================================================

export type WorkflowMode = "workspace" | "build" | "run";

/**
 * Campaign constraints determine which builder steps are required.
 * These encode proven methodology as configurable defaults.
 */
export interface CampaignConstraints {
  /** Campaign follows a defined seasonal arc */
  seasonRequired: boolean;
  /** Campaign has a central narrative with headline, motivation, value proposition */
  coreStoryRequired: boolean;
  /** Campaign uses the 5-stage narrative arc */
  narrativeArcEnforced: boolean;
  /** Campaign produces learning signals - ALWAYS TRUE, cannot be disabled */
  learningLoopEnabled: true;
}

/**
 * Canonical constraints - the default Nexus campaign model
 * Seasonal, story-driven campaigns with learning loop enabled
 */
export const CANONICAL_CONSTRAINTS: CampaignConstraints = {
  seasonRequired: true,
  coreStoryRequired: true,
  narrativeArcEnforced: true,
  learningLoopEnabled: true,
};

/**
 * Minimal constraints for users who want to customize
 * Note: learningLoopEnabled is always true
 */
export const MINIMAL_CONSTRAINTS: CampaignConstraints = {
  seasonRequired: false,
  coreStoryRequired: false,
  narrativeArcEnforced: false,
  learningLoopEnabled: true,
};

export type CampaignModel = "canonical" | "custom";

/**
 * Entry point sources for campaign builds - used for analytics and debugging
 * Add new sources here as entry points are added.
 */
export type BuildEntrySource =
  | "CommandPalette" // Cmd+K > Create Campaign
  | "CampaignView" // Campaigns list > New Campaign button
  | "WorkspaceOverview" // Overview page quick action
  | "CalendarAnchor" // Click on calendar item > Execute
  | "KnowledgeBase" // Knowledge base panel action
  | "BrandHub" // Brand hub quick action
  | "ClientHub" // Client Hub quick action
  | "Onboarding" // Onboarding flow
  | "DeepLink" // Direct URL navigation
  | "Unknown"; // Fallback for untracked sources

/**
 * Build session tracks the state of an in-progress campaign creation
 */
export interface BuildSession {
  /** Campaign ID if editing, undefined if new */
  campaignId?: string;
  /** Campaign name (for display in mode indicator) */
  campaignName?: string;
  /** Whether using canonical or custom model */
  model: CampaignModel;
  /** Active constraints for this build session */
  constraints: CampaignConstraints;
  /** Current step in the builder (0-indexed) */
  step: number;
  /** Total steps based on constraints */
  totalSteps: number;
  /** Whether unsaved changes exist */
  dirty: boolean;
  /** Timestamp when build started */
  startedAt: Date;
  /** Where the build was initiated from (for analytics) */
  source?: BuildEntrySource;
}

export interface BuildConfig {
  campaignId?: string;
  campaignName?: string;
  model?: CampaignModel;
  constraints?: Partial<CampaignConstraints>;
  /** Track where the build was initiated from */
  source?: BuildEntrySource;
}

export type ExitReason = "save" | "cancel" | "launch";

/**
 * Confirmation request for unsaved changes dialog
 */
export interface ConfirmationRequest {
  type: "discard_changes";
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export interface ModeContextType {
  // Current mode
  mode: WorkflowMode;

  // Build mode state
  buildSession: BuildSession | null;
  wizardState: WizardState | null;
  startBuild: (config?: BuildConfig) => void;
  updateBuildSession: (updates: Partial<BuildSession>) => void;
  updateWizardState: (updates: Partial<WizardState>) => void;
  exitBuild: (reason: ExitReason) => Promise<void>;

  // Draft recovery
  hasPendingDraft: boolean;
  pendingDraftInfo: {
    campaignName?: string;
    step: number;
    updatedAt?: Date;
  } | null;
  restoreDraft: () => Promise<void>;
  discardDraft: () => Promise<void>;

  // Save status
  saveStatus: SaveStatus;

  // Confirmation dialog state (for UI to render)
  pendingConfirmation: ConfirmationRequest | null;
  clearConfirmation: () => void;

  // Run mode state
  activeCampaignId: string | null;
  activeCampaignName: string | null;
  enterRun: (campaignId: string, campaignName?: string) => void;
  exitRun: () => void;

  // Transition helpers
  canTransition: (to: WorkflowMode) => boolean;
  requestTransition: (to: WorkflowMode) => Promise<boolean>;

  // Mode labels for UI
  getModeLabel: () => string;
  getModeDescription: () => string;
}

// ============================================================================
// CONTEXT
// ============================================================================

const ModeContext = createContext<ModeContextType | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

// Default wizard state
const DEFAULT_WIZARD_STATE: WizardState = {
  model: "canonical",
  constraints: CANONICAL_CONSTRAINTS,
  basics: {
    name: "",
    description: "",
    goalAmount: 10000,
    startDate: new Date().toISOString().split("T")[0],
    endDate: "",
  },
  season: null,
  coreStory: {},
  narrativeSteps: [],
};

export function ModeProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { currentClient } = useClient();

  // Get current client slug for navigation (null if no client selected)
  const clientSlug = currentClient ? getClientSlug(currentClient) : null;

  // Core state
  const [mode, setMode] = useState<WorkflowMode>("workspace");
  const [buildSession, setBuildSession] = useState<BuildSession | null>(null);
  const [wizardState, setWizardState] = useState<WizardState | null>(null);
  const [activeCampaignId, setActiveCampaignId] = useState<string | null>(null);
  const [activeCampaignName, setActiveCampaignName] = useState<string | null>(
    null,
  );

  // Draft recovery state
  const [hasPendingDraft, setHasPendingDraft] = useState(false);
  const [pendingDraftInfo, setPendingDraftInfo] = useState<{
    campaignName?: string;
    step: number;
    updatedAt?: Date;
  } | null>(null);
  const [pendingDraft, setPendingDraft] = useState<BuildSessionDraft | null>(
    null,
  );

  // Save status
  const [saveStatus, setSaveStatus] = useState<SaveStatus>({
    state: "idle",
    saving: false,
    lastSaved: null,
    error: null,
  });

  // Confirmation dialog state
  const [pendingConfirmation, setPendingConfirmation] =
    useState<ConfirmationRequest | null>(null);

  const clearConfirmation = useCallback(() => {
    setPendingConfirmation(null);
  }, []);

  // Ref to track if we've checked for drafts
  const hasCheckedForDraft = useRef(false);

  // Pending transition for confirmation dialogs (reserved for future use)
  const [_pendingTransition, _setPendingTransition] = useState<{
    to: WorkflowMode;
    resolve: (confirmed: boolean) => void;
  } | null>(null);

  // Register save status callback
  useEffect(() => {
    buildSessionPersistenceService.onSaveStatusChange(setSaveStatus);
  }, []);

  // Check for pending drafts on mount
  useEffect(() => {
    const checkForDraft = async () => {
      if (!user?.id || !currentClient?.id || hasCheckedForDraft.current) return;
      hasCheckedForDraft.current = true;

      try {
        const scope = { userId: user.id, clientId: currentClient.id };
        const draft = await buildSessionPersistenceService.loadDraft(
          currentClient.id,
          user.id,
          scope,
        );

        if (draft) {
          setHasPendingDraft(true);
          setPendingDraftInfo({
            campaignName: draft.campaignName,
            step: draft.step,
            updatedAt: draft.updatedAt,
          });
          setPendingDraft(draft);
        }
      } catch (error) {
        console.error(
          "[ModeContext] Failed to check for pending draft:",
          error,
        );
      }
    };

    void checkForDraft();
  }, [user?.id, currentClient?.id]);

  // Reset draft check when client changes
  useEffect(() => {
    hasCheckedForDraft.current = false;
    setHasPendingDraft(false);
    setPendingDraftInfo(null);
    setPendingDraft(null);
  }, [currentClient?.id]);

  // ============================================================================
  // ROUTE-BASED MODE INFERENCE
  // ============================================================================

  useEffect(() => {
    const path = location.pathname;

    // Build mode routes
    if (path.includes("/build") || path.includes("/campaigns/new")) {
      if (mode !== "build" && !buildSession) {
        // Auto-start a build session if navigating directly to build route
        startBuild({ source: "DeepLink" });
      }
    }
    // Run mode would be detected by campaign status, not route
    // Workspace is the default
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // ============================================================================
  // BUILD MODE
  // ============================================================================

  const calculateTotalSteps = (constraints: CampaignConstraints): number => {
    // Base steps: Model Confirmation (0), Basics (1), Review (always last)
    let steps = 3; // Model + Basics + Review

    if (constraints.seasonRequired) steps++;
    if (constraints.coreStoryRequired) steps++;
    // Narrative arc is always shown, but complexity varies
    steps++; // Narrative arc step

    return steps;
  };

  // Helper to persist current state
  const persistDraft = useCallback(
    (session: BuildSession, wizard: WizardState) => {
      if (!user?.id || !currentClient?.id) return;

      const scope = { userId: user.id, clientId: currentClient.id };
      const draft: BuildSessionDraft = {
        clientId: currentClient.id,
        userId: user.id,
        campaignId: session.campaignId,
        campaignName: session.campaignName,
        model: session.model,
        constraints: session.constraints,
        step: session.step,
        totalSteps: session.totalSteps,
        wizardState: wizard,
        startedAt: session.startedAt,
      };

      buildSessionPersistenceService.autosave(draft, scope);
    },
    [user?.id, currentClient?.id],
  );

  const startBuild = useCallback(
    (config?: BuildConfig) => {
      const model = config?.model ?? "canonical";
      const source = config?.source ?? "Unknown";
      const baseConstraints =
        model === "canonical" ? CANONICAL_CONSTRAINTS : MINIMAL_CONSTRAINTS;
      const constraints: CampaignConstraints = {
        ...baseConstraints,
        ...config?.constraints,
        learningLoopEnabled: true, // Always enforce
      };

      const session: BuildSession = {
        campaignId: config?.campaignId,
        campaignName: config?.campaignName,
        model,
        constraints,
        step: 0,
        totalSteps: calculateTotalSteps(constraints),
        dirty: false,
        startedAt: new Date(),
        source,
      };

      // Log build entry point for analytics (dev only for now)
      if (import.meta.env.DEV) {
        console.info(`[ModeContext] Build started from: ${source}`, {
          campaignId: config?.campaignId,
          model,
        });
      }

      const wizard: WizardState = {
        ...DEFAULT_WIZARD_STATE,
        model,
        constraints,
      };

      setBuildSession(session);
      setWizardState(wizard);
      setMode("build");

      // Clear any pending draft state since we're starting fresh
      setHasPendingDraft(false);
      setPendingDraftInfo(null);
      setPendingDraft(null);

      // Navigate to build or wizard route
      if (!config?.campaignId && clientSlug) {
        // New campaign → wizard
        void navigate(buildClientPath(clientSlug, "campaigns/new"));
      } else if (config?.campaignId && clientSlug) {
        // Edit existing → build route
        void navigate(
          buildClientPath(clientSlug, `campaigns/${config.campaignId}/build`),
        );
      }
    },
    [navigate, clientSlug],
  );

  const updateBuildSession = useCallback((updates: Partial<BuildSession>) => {
    setBuildSession((prev) => {
      if (!prev) return prev;

      const updated = { ...prev, ...updates };

      // Recalculate total steps if constraints changed
      if (updates.constraints) {
        updated.totalSteps = calculateTotalSteps(updated.constraints);
      }

      // Mark as dirty if anything substantive changed
      if (
        updates.campaignName !== undefined ||
        updates.step !== undefined ||
        updates.constraints !== undefined
      ) {
        updated.dirty = true;
      }

      return updated;
    });
  }, []);

  const updateWizardState = useCallback((updates: Partial<WizardState>) => {
    setWizardState((prev) => {
      if (!prev) return prev;
      return { ...prev, ...updates };
    });
  }, []);

  // Auto-save when buildSession or wizardState changes
  useEffect(() => {
    if (mode === "build" && buildSession && wizardState) {
      persistDraft(buildSession, wizardState);
    }
  }, [mode, buildSession, wizardState, persistDraft]);

  // Draft recovery functions
  const restoreDraft = useCallback(async () => {
    if (!pendingDraft) return;

    const session: BuildSession = {
      campaignId: pendingDraft.campaignId,
      campaignName: pendingDraft.campaignName,
      model: pendingDraft.model,
      constraints: pendingDraft.constraints,
      step: pendingDraft.step,
      totalSteps: pendingDraft.totalSteps,
      dirty: true, // Mark as dirty since we're restoring
      startedAt: pendingDraft.startedAt,
    };

    setBuildSession(session);
    setWizardState(pendingDraft.wizardState);
    setMode("build");
    setHasPendingDraft(false);
    setPendingDraftInfo(null);
    setPendingDraft(null);

    // Navigate to build route
    if (clientSlug) {
      void navigate(buildClientPath(clientSlug, "build"));
    }
  }, [pendingDraft, clientSlug, navigate]);

  const discardDraft = useCallback(async () => {
    if (!user?.id || !currentClient?.id) return;

    const scope = { userId: user.id, clientId: currentClient.id };
    await buildSessionPersistenceService.deleteDraft(
      currentClient.id,
      user.id,
      scope,
    );

    setHasPendingDraft(false);
    setPendingDraftInfo(null);
    setPendingDraft(null);
  }, [user?.id, currentClient?.id]);

  const exitBuild = useCallback(
    async (reason: ExitReason): Promise<void> => {
      if (!buildSession) {
        setMode("workspace");
        setWizardState(null);
        return;
      }

      // Handle different exit reasons
      switch (reason) {
        case "save":
          // Force save to database before exiting
          if (user?.id && currentClient?.id && wizardState) {
            const scope = { userId: user.id, clientId: currentClient.id };
            const draft: BuildSessionDraft = {
              clientId: currentClient.id,
              userId: user.id,
              campaignId: buildSession.campaignId,
              campaignName: buildSession.campaignName,
              model: buildSession.model,
              constraints: buildSession.constraints,
              step: buildSession.step,
              totalSteps: buildSession.totalSteps,
              wizardState,
              startedAt: buildSession.startedAt,
            };
            await buildSessionPersistenceService.forceSave(draft, scope);
          }
          break;

        case "cancel":
          // Delete the draft when explicitly cancelling
          if (user?.id && currentClient?.id) {
            const scope = { userId: user.id, clientId: currentClient.id };
            await buildSessionPersistenceService.deleteDraft(
              currentClient.id,
              user.id,
              scope,
            );
          }
          break;

        case "launch":
          // Campaign was launched - delete draft and transition to Run mode
          if (user?.id && currentClient?.id) {
            const scope = { userId: user.id, clientId: currentClient.id };
            await buildSessionPersistenceService.deleteDraft(
              currentClient.id,
              user.id,
              scope,
            );
          }

          if (buildSession.campaignId && clientSlug) {
            setActiveCampaignId(buildSession.campaignId);
            setActiveCampaignName(buildSession.campaignName ?? null);
            setBuildSession(null);
            setWizardState(null);
            setMode("run");
            void navigate(
              buildClientPath(clientSlug, "campaigns", buildSession.campaignId),
            );
            return;
          }
          break;
      }

      // Default: return to client home or client list
      setBuildSession(null);
      setWizardState(null);
      setMode("workspace");
      if (clientSlug) {
        void navigate(buildClientPath(clientSlug));
      } else {
        void navigate("/clients");
      }
    },
    [
      buildSession,
      wizardState,
      navigate,
      clientSlug,
      user?.id,
      currentClient?.id,
    ],
  );

  // ============================================================================
  // RUN MODE
  // ============================================================================

  const enterRun = useCallback(
    (campaignId: string, campaignName?: string) => {
      // If in build mode with unsaved changes, show confirmation
      if (mode === "build" && buildSession?.dirty) {
        setPendingConfirmation({
          type: "discard_changes",
          message:
            "You have unsaved changes in your campaign builder. Switching to Run mode will discard them. Continue?",
          onConfirm: () => {
            setPendingConfirmation(null);
            setBuildSession(null);
            setWizardState(null);
            setActiveCampaignId(campaignId);
            setActiveCampaignName(campaignName ?? null);
            setMode("run");
          },
          onCancel: () => {
            setPendingConfirmation(null);
          },
        });
        return;
      }

      setBuildSession(null);
      setWizardState(null);
      setActiveCampaignId(campaignId);
      setActiveCampaignName(campaignName ?? null);
      setMode("run");
    },
    [mode, buildSession],
  );

  const exitRun = useCallback(() => {
    setActiveCampaignId(null);
    setActiveCampaignName(null);
    setMode("workspace");
    if (clientSlug) {
      void navigate(buildClientPath(clientSlug));
    } else {
      void navigate("/clients");
    }
  }, [navigate, clientSlug]);

  // ============================================================================
  // TRANSITIONS
  // ============================================================================

  const canTransition = useCallback(
    (to: WorkflowMode): boolean => {
      // From workspace, can go anywhere
      if (mode === "workspace") return true;

      // From build, need to handle dirty state
      if (mode === "build") {
        if (to === "workspace" && buildSession?.dirty) {
          return true; // Can transition, but will need confirmation
        }
        return true;
      }

      // From run, can go back to workspace or into build (edit)
      if (mode === "run") {
        return true;
      }

      return true;
    },
    [mode, buildSession],
  );

  const requestTransition = useCallback(
    async (to: WorkflowMode): Promise<boolean> => {
      if (!canTransition(to)) return false;

      // Check if confirmation is needed for dirty build state
      if (mode === "build" && buildSession?.dirty && to !== "run") {
        return new Promise((resolve) => {
          setPendingConfirmation({
            type: "discard_changes",
            message:
              "You have unsaved changes. Are you sure you want to leave? Your progress will be lost.",
            onConfirm: async () => {
              setPendingConfirmation(null);
              // Perform the transition after confirmation
              if (to === "workspace") {
                await exitBuild("cancel");
              } else if (to === "build") {
                // Unknown source since this is an internal mode transition
                startBuild({ source: "Unknown" });
              }
              resolve(true);
            },
            onCancel: () => {
              setPendingConfirmation(null);
              resolve(false);
            },
          });
        });
      }

      // Perform the transition
      switch (to) {
        case "workspace":
          if (mode === "build") {
            await exitBuild("cancel");
          } else if (mode === "run") {
            exitRun();
          }
          break;

        case "build":
          // Unknown source since this is an internal mode transition
          startBuild({ source: "Unknown" });
          break;

        case "run":
          // Run mode is entered via enterRun with a campaign ID
          break;
      }

      return true;
    },
    [mode, buildSession, canTransition, exitBuild, exitRun, startBuild],
  );

  // ============================================================================
  // LABELS
  // ============================================================================

  const getModeLabel = useCallback((): string => {
    switch (mode) {
      case "workspace":
        return "Explore";
      case "build":
        return "Build Campaign";
      case "run":
        return "Live Campaign";
    }
  }, [mode]);

  const getModeDescription = useCallback((): string => {
    switch (mode) {
      case "workspace":
        return "Understand what needs attention";
      case "build":
        return "Design and commit to a campaign plan";
      case "run":
        return "Execute and observe reality";
    }
  }, [mode]);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  const value: ModeContextType = {
    mode,
    buildSession,
    wizardState,
    startBuild,
    updateBuildSession,
    updateWizardState,
    exitBuild,
    hasPendingDraft,
    pendingDraftInfo,
    restoreDraft,
    discardDraft,
    saveStatus,
    pendingConfirmation,
    clearConfirmation,
    activeCampaignId,
    activeCampaignName,
    enterRun,
    exitRun,
    canTransition,
    requestTransition,
    getModeLabel,
    getModeDescription,
  };

  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
}

// ============================================================================
// HOOK
// ============================================================================

export function useMode(): ModeContextType {
  const context = useContext(ModeContext);
  if (!context) {
    throw new Error("useMode must be used within ModeProvider");
  }
  return context;
}

// ============================================================================
// UTILITIES
// ============================================================================

/**
 * Get display-friendly step name based on constraints
 */
export function getStepName(
  stepIndex: number,
  constraints: CampaignConstraints,
): string {
  const steps: string[] = ["Campaign Model", "Campaign Basics"];

  if (constraints.seasonRequired) {
    steps.push("Campaign Season");
  }
  if (constraints.coreStoryRequired) {
    steps.push("Core Story");
  }
  steps.push("Narrative Arc");
  steps.push("Review & Launch");

  return steps[stepIndex] ?? "Unknown Step";
}

/**
 * Check if a specific step should be shown based on constraints
 */
export function isStepVisible(
  stepKey: string,
  constraints: CampaignConstraints,
): boolean {
  switch (stepKey) {
    case "model":
    case "basics":
    case "narrative":
    case "review":
      return true;
    case "season":
      return constraints.seasonRequired;
    case "story":
      return constraints.coreStoryRequired;
    default:
      return false;
  }
}

// Re-export WizardState type for consumers
export type {
  WizardState,
  SaveStatus,
  SaveState,
} from "../services/buildSessionPersistenceService";
